import type { FeatureCollection } from "geojson";
import { FillLayerSpecification, Map as GlMap, LineLayerSpecification } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { useCallback, useContext } from "react";
import Map, {
  AttributionControl,
  GeolocateControl,
  Layer,
  MapRef,
  NavigationControl,
  ScaleControl,
  Source,
} from "react-map-gl/maplibre";
import { useLocation, useNavigate } from "react-router-dom";
import { CaMapContext, useLayers, useMapLabels, useSelected } from "../state";
import "./CaMap.css";
import { fake, ImageOverlayControl, transparentPng } from "./controls/ImageOverlayControl";
import { LABEL_MINZOOM, LabelSwitchControl } from "./controls/LabelSwitchControl";
import { LayersControl } from "./controls/LayersControl";
import { ProjectControl } from "./controls/ProjectControl";
import { SearchControl } from "./controls/SearchControl";
import { BuildingsControl, FootprintsControl, SelectControl } from "./controls/SelectControl";
import { projectFillStyle, projectStyle } from "./layerStyles";
import { PARCEL_TILES } from "./pmtiles";
import { searchDotsLayer, searchMatchLayer } from "./searchResultLayers";
import { defaultViewState } from "./viewstate";

const layer = "default";
const beforeId = "waterway_line_label"; //"label-address-housenumber"
const parcelLayer = (
  <Layer
    id="parcel"
    source-layer={layer}
    beforeId={beforeId}
    type="fill"
    paint={{
      "fill-outline-color": "rgb(7, 119, 53)",
      "fill-color": [
        "case",
        ["boolean", ["feature-state", "hover"], false],
        "rgba(219,247,227, 0.6)",
        "rgba(0,0,0,0)",
      ],
    }}
  />
);

const ParcelLine = ({ ...props }) => {
  const aerial = useLayers((s) => s.backgroundId !== "default");
  return (
    <Layer
      id="parcelLine"
      beforeId={beforeId}
      type="line"
      paint={{
        "line-color": aerial ? "rgb(239, 201, 2)" : "rgb(7, 119, 53)",
        "line-width": ["interpolate", ["linear"], ["zoom"], 10, 0.2, 18, 1.5],
      }}
      {...props}
    />
  );
};

const labelPaint = {
  "text-color": "#666",
  "text-halo-blur": 1,
  "text-halo-color": "rgba(255,255,255,0.75)",
  "text-halo-width": 1,
};
const labelTextSize = ["interpolate", ["linear"], ["zoom"], 10, 4, 20, 14];

// Kadastrale aanduidingen óf huisnummers, geschakeld via de
// LabelSwitchControl (rechtsonder). key={mode} forceert een remount:
// react-map-gl kan source-layer niet in-place wijzigen.
const ParcelLabels = ({ ...props }) => {
  // NB: net als ParcelLine de door <Source> geïnjecteerde `source`-prop
  // doorgeven — cloneElement bereikt alleen directe children
  const mode = useMapLabels((s) => s.mode);
  if (mode === "housenr")
    return (
      <Layer
        key="housenr"
        id="parcelLabels"
        type="symbol"
        source-layer="housenr"
        layout={{
          "text-field": "{number}",
          "text-font": ["Noto Sans Regular"],
          // @ts-ignore
          "text-size": labelTextSize,
        }}
        minzoom={LABEL_MINZOOM}
        paint={labelPaint}
        {...props}
      />
    );
  return (
    <Layer
      key="cadastre"
      id="parcelLabels"
      type="symbol"
      source-layer="label"
      layout={{
        "text-rotation-alignment": "map",
        "text-anchor": "top",
        "text-field": ["step", ["zoom"], ["get", "short_name"], 17.5, ["get", "id"]],
        "text-font": ["Noto Sans Regular"],
        // @ts-ignore
        "text-size": labelTextSize,
      }}
      minzoom={LABEL_MINZOOM}
      paint={labelPaint}
      {...props}
    />
  );
};
// https://api.pdok.nl/lv/bgt/ogc/v0_1/styles/achtergrondvisualisatie?f=mapbox
// https://medium.com/@yixu0215/dynamic-label-placement-with-mapbox-gl-js-turf-polylabel-1f84f1d4bf6b
// https://github.com/Roxyi/dynamic_labels/blob/master/js/map.js

// fill
const selectionFillStyle: Partial<FillLayerSpecification> = {
  paint: {
    "fill-outline-color": "rgb(7, 119, 53)",
    "fill-color": "rgba(250, 221, 139, 0.6)",
  },
};
const selectionStyle: Partial<LineLayerSpecification> = {
  paint: {
    "line-color": "rgb(7, 119, 53)",
    "line-width": 3,
    "line-dasharray": [3, 2],
  },
};
const buildingStyle: Partial<FillLayerSpecification> = {
  paint: {
    "fill-outline-color": "rgb(7, 10, 10)",
    "fill-color": [
      "case",
      ["boolean", ["feature-state", "selected"], false],
      "rgba(250, 221, 139, 0.7)",
      "rgba(30, 30, 30, 0.3)",
    ],
  },
};

// Bouwvlak uit het bestemmingsplan: de kleur die de cube meegeeft (#e57373),
// licht gevuld zodat de luchtfoto eronder leesbaar blijft.
const footprintStyle: Partial<FillLayerSpecification> = {
  paint: {
    "fill-outline-color": "rgb(229, 115, 115)",
    "fill-color": [
      "case",
      ["boolean", ["feature-state", "selected"], false],
      "rgba(229, 115, 115, 0.6)",
      "rgba(229, 115, 115, 0.25)",
    ],
  },
};

const useHover = (map: GlMap) =>
  useCallback(
    (e) => {
      if (!map) return;
      if (hoveredId) {
        map.setFeatureState(
          { source: "parcels", sourceLayer: layer, id: hoveredId },
          { hover: false }
        );
      }
      hoveredId = e.type === "mousemove" && e.features?.[0]?.id;
      if (hoveredId) {
        map.setFeatureState(
          { source: "parcels", sourceLayer: layer, id: hoveredId },
          { hover: true }
        );
      }
      map.getCanvas().style.cursor = hoveredId ? "pointer" : "grab";
    },
    [map]
  );

const parcels = ["parcel"];
let hoveredId = null; // state tracking

const initialViewState = defaultViewState();

const noData: FeatureCollection = {
  type: "FeatureCollection",
  features: [],
};
export const CAMap = ({ className = undefined }) => {
  const [map, setMap] = useContext(CaMapContext);
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const onHover = useHover(map);
  const toggleSelected = useSelected((s) => s.toggle);

  const onClick = useCallback(
    ({ point, lngLat, target }) => {
      // Clicking a building feature selects it instead of navigating. The
      // buildings layer is only queryable while visible (base section).
      const building =
        target.getLayer("buildings") &&
        target.queryRenderedFeatures(point, { layers: ["buildings"] })[0];
      if (building) {
        toggleSelected("building", String(building.properties?.extId ?? building.id));
        return;
      }
      // Coming from /search (or still browsing parcels that trace back to it):
      // tag the navigation so the report shows "Terug naar zoeken"
      const fromSearch =
        routerLocation.pathname.startsWith("/search") ||
        Boolean((routerLocation.state as any)?.fromSearch);
      navigate(`/location/${lngLat.lng.toFixed(5)},${lngLat.lat.toFixed(5)}`, {
        state: fromSearch ? { fromSearch: true } : undefined,
      });
    },
    [navigate, toggleSelected, routerLocation]
  );

  // @ts-ignore:next-line
  const refCallBack = useCallback<Ref>(
    // @ts-ignore:next-line
    (r: MapRef) => {
      if (!r) return;
      const m = r.getMap();
      setMap((window["map"] = m));
      // The OpenFreeMap "bright" style references POI icons that aren't in
      // its sprite (bicycle_parking, atm, sports_centre, ...). Provide an
      // empty placeholder so MapLibre stops logging warnings.
      m.on("styleimagemissing", (e) => {
        if (m.hasImage(e.id)) return;
        m.addImage(e.id, { width: 1, height: 1, data: new Uint8Array(4) });
      });
    },
    [setMap]
  );
  return (
    <div className={className}>
      <Map
        initialViewState={initialViewState}
        mapStyle="https://tiles.openfreemap.org/styles/bright"
        attributionControl={false}
        ref={refCallBack}
        interactiveLayerIds={parcels}
        onClick={onClick}
        onMouseMove={onHover}
        onMouseLeave={onHover}
      >
        <NavigationControl position="top-right" />
        <GeolocateControl position="top-right" />
        <AttributionControl />
        <ScaleControl />
        <Source id="baoverlay" type="image" url={transparentPng} coordinates={fake}>
          <Layer id="baoverlay" type="raster" layout={{ visibility: "none" }} />
          <ImageOverlayControl id="baoverlay" />
        </Source>
        <LayersControl />
        <LabelSwitchControl />
        {/* Zoekresultaten: bolletjes uitgezoomd, rode percelen ingezoomd; de
            wissel zit in maxzoom/minzoom, zie searchResultLayers.ts. */}
        <Source id="searchResults" type="geojson" data={noData}>
          <Layer id="searchResults" type="circle" {...searchDotsLayer} />
          <SearchControl />
        </Source>
        <Source id="parcels" type="vector" promoteId="id" url={PARCEL_TILES}>
          {parcelLayer}
          <Layer id="searchMatch" source-layer={layer} type="fill" {...searchMatchLayer} />
          <ParcelLine source="parcels" source-layer={layer} />
          <ParcelLabels />
        </Source>
        <Source id="selection" type="geojson" data={noData}>
          <Layer id="selection" type="line" {...selectionStyle} />
          <Layer id="selectionFill" type="fill" {...selectionFillStyle} />
          <SelectControl id="selection" />
        </Source>
        <Source id="projects" type="geojson" data={noData}>
          <Layer id="projects" type="line" {...projectStyle} />
          <Layer id="projectsFill" type="fill" {...projectFillStyle} />
          <ProjectControl />
        </Source>
        <Source id="buildings" type="geojson" data={noData} promoteId="extId">
          <Layer id="buildings" type="fill" {...buildingStyle} />
          <BuildingsControl id="buildings" />
        </Source>
        <Source id="footprints" type="geojson" data={noData} promoteId="index">
          <Layer id="footprints" type="fill" beforeId="buildings" {...footprintStyle} />
          <FootprintsControl id="footprints" />
        </Source>
      </Map>
    </div>
  );
};
